/**
 * Pure date/grouping helpers for the resident Todo Calendar.
 * All functions take dates as arguments — none read the clock, so tests can
 * pin any date. Native Date + Intl only (repo convention; no date-fns).
 */
import type { ResidentTodo } from '@/types/residents/todos';
import { compareTodos, formatTime } from './todoTime';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Local calendar date -> 'YYYY-MM-DD'. Never uses toISOString (UTC shift). */
export const toDateKey = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** 'YYYY-MM-DD' -> local midnight Date. */
export const parseDateKey = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Sunday-start week. */
export const startOfWeek = (d: Date): Date => addDays(d, -d.getDay());

export const getWeekDays = (d: Date): Date[] => {
  const first = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
};

/** e.g. "Wed, Aug 5". Locale is pinned to en-US to match the repo's other
 *  formatters (see utils/residents/date.ts) and to keep tests deterministic —
 *  the runtime default would vary by machine. */
export const formatDayHeading = (d: Date): string =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export const groupTodosByDateKey = (
  todos: ResidentTodo[],
): Record<string, ResidentTodo[]> => {
  const groups: Record<string, ResidentTodo[]> = {};
  for (const t of todos) {
    (groups[t.dueDate] ??= []).push(t);
  }
  return groups;
};

/** A day's order (todoTime compareTodos): timed todos first, earliest first, then the rest A–Z.
 *  Completion is deliberately NOT a sort key: a row a resident ticks keeps its slot instead of
 *  dropping to the bottom of the day, so the list never reshuffles under their finger. */
export const sortTodos = (todos: ResidentTodo[]): ResidentTodo[] => [...todos].sort(compareTodos);

/** First day (local midnight) of d's month. */
export const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1);

/** First day of the month `delta` months away. Anchoring to the 1st makes
 *  day-clamping (Jan 31 → "Feb 31") impossible by construction. */
export const addMonthsToStart = (d: Date, delta: 1 | -1): Date =>
  new Date(d.getFullYear(), d.getMonth() + delta, 1);

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** e.g. "August 2026". Locale pinned to en-US like the other formatters. */
export const formatMonthLabel = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/** Sunday-start weeks covering d's month, padded with real adjacent-month
 *  days (4–6 rows × 7). Every cell is a local-midnight Date. */
export const getMonthMatrix = (d: Date): Date[][] => {
  const nextMonthFirst = addMonthsToStart(d, 1);
  const weeks: Date[][] = [];
  let cursor = startOfWeek(startOfMonth(d));
  do {
    weeks.push(getWeekDays(cursor));
    cursor = addDays(cursor, 7);
  } while (cursor < nextMonthFirst);
  return weeks;
};

/** Day-cell a11y label, e.g. "Wednesday, August 5, 1 to do". Shared by the
 *  Planner's DayCell (its strip and its month) and the home WeekStrip so a
 *  date reads identically whichever one a screen-reader user is on.
 *
 *  "N to do", not "N todo(s)": `count` is REMAINING work — both callers pass
 *  incomplete-only counts — so total-phrasing would announce "0 todos" on a
 *  day whose items are merely all ticked off, telling a screen-reader user to
 *  skip a cell that still opens a populated list. It also has no plural to get
 *  wrong at 0 or 1. */
export const formatDayAccessibilityLabel = (d: Date, count: number): string => {
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return `${datePart}, ${count} to do`;
};

/** e.g. "Thu, Dec 31, 2026" — for an end date that can sit months away. */
export const formatDateWithYear = (d: Date): string =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

/** Whole calendar days from `from` to `to`: local Y/M/D through UTC, so a DST day still counts 1. */
const daysBetween = (from: Date, to: Date): number =>
  Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      86_400_000,
  );

/** The Planner's title for a day: "Today", "Tomorrow", "Yesterday", else the weekday ("Friday"). */
export const relativeDayName = (d: Date, today: Date): string => {
  const diff = daysBetween(today, d);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long' });
};

/** The line under the title: "Wednesday, October 14" under a relative title, "October 16" when the
 *  title is already the weekday; a year other than today's is appended. */
export const formatDateLine = (d: Date, today: Date): string => {
  const relative = Math.abs(daysBetween(today, d)) <= 1;
  return d.toLocaleDateString('en-US', {
    ...(relative ? { weekday: 'long' as const } : {}),
    month: 'long',
    day: 'numeric',
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' as const }),
  });
};

/** "Oct 3", or "Dec 28, 2025" in another year. */
const shortDate = (d: Date, today: Date): string =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' as const }),
  });

/** The overdue line's tail, from its OLDEST to-do: "since yesterday", "since Monday" (2–6 days
 *  ago), "since Oct 3", "since Dec 28, 2025". */
export const overdueSince = (oldestKey: string, todayKey: string): string => {
  const d = parseDateKey(oldestKey);
  const today = parseDateKey(todayKey);
  const days = daysBetween(d, today);
  if (days <= 1) return 'since yesterday';
  if (days <= 6) return `since ${d.toLocaleDateString('en-US', { weekday: 'long' })}`;
  return `since ${shortDate(d, today)}`;
};

/** An overdue row's date: "Yesterday", "Mon" (2–6 days ago), "Oct 3", "Dec 28, 2025" — then its
 *  time when it has one ("Mon 7:30 AM", "Oct 3, 6:00 PM"). */
export const overdueShortLabel = (todo: ResidentTodo, todayKey: string): string => {
  const d = parseDateKey(todo.dueDate);
  const today = parseDateKey(todayKey);
  const days = daysBetween(d, today);
  const recent = days <= 6;
  const day =
    days <= 1
      ? 'Yesterday'
      : recent
        ? d.toLocaleDateString('en-US', { weekday: 'short' })
        : shortDate(d, today);
  if (!todo.dueTime) return day;
  const time = formatTime(todo.dueTime);
  return recent ? `${day} ${time}` : `${day}, ${time}`;
};
